import React from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

import {
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  CustomInput,
  Table,
  Row,
} from "reactstrap";
import { Colxx } from "../../components/common/CustomBootstrap";
import IntlMessages from "../../helpers/IntlMessages";
import { AddCommaNumber } from "../../helpers/Utils";
import { injectIntl } from "react-intl";
import Select from "react-select";
import {
  getFeeApplicationBase,
  getPostDiscountSubtotal,
} from "../../helpers/OrderAdjustmentCalculations";
import { getFeeOtherLabel } from "../../helpers/FeeOtherLabel";

const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
  <div
    className="d-flex align-items-center cursor-pointer badge badge-outline-primary px-3 py-1"
    title="Thay đổi ngày đơn hàng"
    onClick={onClick}
    ref={ref}
    style={{
      minWidth: "140px",
      justifyContent: "center",
      paddingLeft: value ? "26px" : undefined,
      paddingRight: value ? "26px" : undefined,
    }}
  >
    <i className="simple-icon-clock mr-1" style={{ fontSize: "14px" }} />
    <span style={{ fontSize: "0.82rem", fontWeight: "bold" }}>{value || "Hôm nay"}</span>
  </div>
));

const formatOptionLabel = ({ label, icon }) => (
  <div className="d-flex align-items-center">
    {icon && <i className={`${icon} mr-2`} style={{ fontSize: "16px" }} />}
    <span>{label}</span>
  </div>
);

const OrderDetailBadges = ({ order }) => {
  const hasDetails =
    (order?.size && order.size !== "") ||
    (order?.color && order.color !== "") ||
    (order?.material && order.material !== "") ||
    (order?.flavor && order.flavor !== "") ||
    (order?.selectedAttributes &&
      Object.keys(order.selectedAttributes).some(
        (key) => !["size", "color", "material"].includes(key),
      )) ||
    (order.sizeSelectedId?.length && order.sizeSettings) ||
    (order.flavorSelectedId?.length && order.flavorSettings) ||
    (order.toppingSelectedId?.length && order.toppingSettings) ||
    (order.iceSelectedValue && order.iceSelectedValue !== "100") ||
    (order.sugarSelectedValue && order.sugarSelectedValue !== "100") ||
    (parseFloat(order?.tax_rate || order?.product?.tax_rate || 0) > 0) ||
    order?.note;

  if (!hasDetails) return null;

  return (
    <tr>
      <td colSpan="5" className="border-top-none pt-0 pb-1 pl-4">
        {/* Note */}
        {order?.note && (
          <Badge className="mr-1 mb-1" color="success">
            {order.note}
          </Badge>
        )}
        {/* Size (static) */}
        {!order.sizeSelectedId?.length && order?.size && (
          <Badge className="mr-1 mb-1" color="primary">
            {order.size}
          </Badge>
        )}
        {/* Color */}
        {order?.color && (
          <Badge className="mr-1 mb-1" color="primary">
            {order.color}
          </Badge>
        )}
        {/* Material */}
        {order?.material && (
          <Badge className="mr-1 mb-1" color="primary">
            {order.material}
          </Badge>
        )}
        {/* Dynamic Attributes */}
        {order?.selectedAttributes &&
          Object.entries(order.selectedAttributes).map(([key, attr]) => {
            if (["size", "color", "material"].includes(key)) return null;
            return (
              <Badge key={key} className="mr-1 mb-1" color="primary">
                {attr.label || attr.value}
              </Badge>
            );
          })}
        {/* Size (dynamic) */}
        {(() => {
          if (!order.sizeSelectedId?.length || !order.sizeSettings) return null;
          const selectedSize = order.sizeSettings.find((s) =>
            order.sizeSelectedId.some((id) => id == s.id),
          );
          if (!selectedSize) return null;
          return (
            <Badge className="mr-1 mb-1" color="primary">
              {selectedSize.name}
            </Badge>
          );
        })()}
        {/* Flavor */}
        {(() => {
          if (order.flavorSelectedId?.length && order.flavorSettings) {
            const selectedFlavor = order.flavorSettings.find((flavor) =>
              order.flavorSelectedId.some((id) => id == flavor.id),
            );
            if (selectedFlavor) {
              return (
                <Badge className="mr-1 mb-1" color="info">
                  Vị: {selectedFlavor.name}
                </Badge>
              );
            }
          }

          if (order?.flavor) {
            return (
              <Badge className="mr-1 mb-1" color="info">
                Vị: {order.flavor.split(",")[0]}
              </Badge>
            );
          }
          return null;
        })()}
        {/* Topping (dynamic) */}
        {(() => {
          if (!order.toppingSelectedId?.length || !order.toppingSettings)
            return null;
          return order.toppingSettings
            .filter((t) => order.toppingSelectedId.some((id) => id == t.id))
            .map((topping, idx) => (
              <Badge color="success" className="mr-1 mb-1" key={idx}>
                {topping.name}
              </Badge>
            ));
        })()}
        {/* Ice */}
        {order.iceSelectedValue &&
          order.iceSelectedValue !== "100" &&
          order.iceSelectedValue !== undefined && (
            <Badge className="mr-1 mb-1" color="warning">
              <IntlMessages id="home.order.ice" />: {order.iceSelectedValue}%
            </Badge>
          )}
        {/* Sugar */}
        {order.sugarSelectedValue &&
          order.sugarSelectedValue !== "100" &&
          order.sugarSelectedValue !== undefined && (
            <Badge className="mr-1 mb-1" color="info">
              <IntlMessages id="home.order.sugar" />: {order.sugarSelectedValue}
              %
            </Badge>
          )}
        {/* Tax Rate */}
        {(() => {
          const itemTax = parseFloat(order?.tax_rate || order?.product?.tax_rate || 0);
          if (itemTax > 0) {
            return (
              <Badge className="mr-1 mb-1" color="secondary">
                Thuế: {itemTax}%
              </Badge>
            );
          }
          return null;
        })()}
      </td>
    </tr>
  );
};

const renderListOrders = ({
  orders,
  incrementItem,
  decrementItem,
  onRemoveOrder,
  removeOrders,
}) => {
  if (!orders.length) return null;

  return (
    <Table className="table mb-0">
      <thead>
        <tr>
          <th>#</th>
          <th className="text-muted text-center text-extra-small mb-2">
            <IntlMessages id="home.label.product-name" />
          </th>
          <th className="text-muted text-center text-extra-small mb-2">
            <IntlMessages id="home.label.quantity" />
          </th>
          <th className="text-center text-muted text-extra-small mb-2">
            <IntlMessages id="home.label.price" />
          </th>
          <th
            className="text-right text-muted text-extra-small mb-2 cursor-pointer"
            onClick={removeOrders}
          >
            <IntlMessages id="home.label.delete" />
          </th>
        </tr>
      </thead>
      <tbody>
        {orders.map((order, idx) => (
          <React.Fragment key={order.id || idx}>
            <tr>
              <td>{idx + 1}</td>
              <td onClick={() => incrementItem(order)}>
                {order.name || order?.product?.name || order?.product_name || ""}
              </td>
              <td className="text-center" onClick={() => decrementItem(order)}>
                {order.count}
              </td>
              <td className="text-center">{AddCommaNumber(order.price)}</td>
              <td
                className="text-right cursor-pointer"
                onClick={() => onRemoveOrder(order.id)}
              >
                <i className="simple-icon-trash" />
              </td>
            </tr>
            <OrderDetailBadges order={order} />
            <tr>
              <td colSpan="5" className="p-0">
                <hr
                  className="m-0"
                  style={{ borderTop: "1px solid #e8e8e8" }}
                />
              </td>
            </tr>
          </React.Fragment>
        ))}
      </tbody>
    </Table>
  );
};

const ProductOrderModalMenu = ({
  isOpen,
  onToggleModal,
  orders,
  incrementItem,
  decrementItem,
  phone,
  name,
  customer_debt,
  onChange,
  onRemoveOrder,
  removeOrders,
  handleSubmit,
  calTotalPrice,
  refund_to_customer,
  intl,
  // New props for enhanced mobile experience
  paymentMethod,
  onChangePaymentMethod,
  shippingCarrier,
  onChangeShippingCarrier,
  connected_providers = [],
  discount_form,
  tax_fee,
  fee_other,
  getTotalDiscountPrice,
  getTotalTaxFeePrice,
  getTotalFeeOtherPrice,
  handleToggleModalDiscountTaxFee,
  handleSaveProvisional,
  info_table,
  shipping_fee = 0,
  staffList,
  selectedStaff,
  onChangeStaff,
  isCashierAccount,
  eInvoiceEnabled,
  issueEInvoice,
  onChangeIssueEInvoice,
  onOpenEInvoiceInfoModal,
  customOrderDate,
  onChangeCustomOrderDate,
}) => {
  const { messages } = intl;
  let totalPrice = calTotalPrice(orders);

  // Discount/Tax/Fee calculations
  const total_discount_price =
    getTotalDiscountPrice && discount_form
      ? getTotalDiscountPrice(orders, discount_form)
      : 0;
  const adjustmentPercentageBase = getPostDiscountSubtotal(
    totalPrice,
    total_discount_price,
  );
  const total_taxfee_price =
    getTotalTaxFeePrice && tax_fee
      ? getTotalTaxFeePrice(orders, tax_fee, adjustmentPercentageBase)
      : 0;
  const total_taxfee_payable =
    tax_fee && tax_fee.calculation === "included" ? 0 : total_taxfee_price;
  const feePercentageBase = getFeeApplicationBase(
    totalPrice,
    adjustmentPercentageBase,
    fee_other && fee_other.applyOn,
  );
  const total_fee_other_price =
    getTotalFeeOtherPrice && fee_other
      ? getTotalFeeOtherPrice(orders, fee_other, feePercentageBase)
      : 0;

  const hasDiscount =
    discount_form &&
    discount_form.saved &&
    discount_form.value &&
    discount_form.type;
  const hasTax = (tax_fee && tax_fee.saved && tax_fee.value && tax_fee.type) || total_taxfee_price > 0;
  const hasFee = fee_other && fee_other.saved && fee_other.value;
  const feeOtherLabel = getFeeOtherLabel(messages, fee_other);
  const hasShipping = shipping_fee > 0;
  const hasAdjustments = hasDiscount || hasTax || hasFee || hasShipping;

  const totalPayment =
    totalPrice -
    total_discount_price +
    (total_taxfee_payable || 0) +
    (total_fee_other_price || 0) +
    (shipping_fee || 0);

  // Payment method options
  const PAYMENT_METHOD_OPTIONS = [
    {
      value: "cash",
      label: messages["home.option.payment.cash"] || "Tiền mặt",
      icon: "simple-icon-wallet",
    },
    {
      value: "transfer",
      label: messages["home.option.payment.transfer"] || "Chuyển khoản",
      icon: "iconsminds-bank",
    },
    {
      value: "card",
      label: messages["home.option.payment.card"] || "Thẻ",
      icon: "simple-icon-credit-card",
    },
    {
      value: "credit",
      label: messages["home.option.payment.credit"] || "Ghi nợ",
      icon: "simple-icon-notebook",
    },
  ];

  // Shipping options (only connected providers)
  const SHIPPING_CARRIER_OPTIONS = [
    {
      value: "ghn",
      label: messages["home.shipping.ghn"] || "GHN",
      icon: "iconsminds-scooter",
    },
    {
      value: "ghtk",
      label: messages["home.shipping.ghtk"] || "GHTK",
      icon: "iconsminds-box-close",
    },
    {
      value: "viettelpost",
      label: messages["home.shipping.viettelpost"] || "Viettel Post",
      icon: "iconsminds-post-mail",
    },
  ];

  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: "0.5rem",
      minHeight: "36px",
      fontSize: "0.85rem",
      boxShadow: "none",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#145388"
        : state.isFocused
          ? "#e6f0f7"
          : "white",
      color: state.isSelected ? "white" : "#333",
      fontSize: "0.85rem",
    }),
    singleValue: (base) => ({
      ...base,
      display: "flex",
      alignItems: "center",
    }),
  };

  const headerTitle = info_table
    ? `${messages["home.mobile.table"] || "Bàn"} ${info_table.item?.identify || ""} - ${messages["pages.order-new-modal-title"] || "Đơn hàng"}`
    : messages["pages.order-new-modal-title"] || "Đơn hàng";

  return (
    <Modal
      isOpen={isOpen}
      toggle={onToggleModal}
      wrapClassName="modal-right"
      backdrop={true}
    >
      <ModalHeader toggle={onToggleModal} className="border-bottom-0 p-3">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div className="d-flex align-items-center">
            <div className="mr-2 position-relative d-inline-block">
              <DatePicker
                selected={
                  customOrderDate ? moment(customOrderDate) : null
                }
                onChange={(date) =>
                  onChangeCustomOrderDate && onChangeCustomOrderDate(date)
                }
                dateFormat="DD/MM/YYYY"
                isClearable={true}
                customInput={<CustomDateInput />}
                popperPlacement="bottom-start"
                popperModifiers={{
                  preventOverflow: {
                    enabled: true,
                    escapeWithReference: false,
                    boundariesElement: "viewport",
                  },
                }}
              />
            </div>
            <span>{headerTitle}</span>
          </div>
        </div>
      </ModalHeader>
      <ModalBody className="pad-model-order-preview table-right scroll">
        <PerfectScrollbar
          options={{
            suppressScrollX: true,
            wheelPropagation: false,
          }}
          id="table-orders"
        >
          {renderListOrders({
            orders,
            incrementItem,
            decrementItem,
            onRemoveOrder,
            removeOrders,
          })}

          {/* Summary Section */}
          <div className="px-3 pt-3">
            {/* Subtotal */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">
                <IntlMessages id="home.summary.total" />
              </span>
              <strong>{AddCommaNumber(totalPrice)}</strong>
            </div>

            {/* Discount */}
            <div
              className="d-flex justify-content-between align-items-center mb-2 cursor-pointer"
              onClick={() => handleToggleModalDiscountTaxFee("discount")}
            >
              <span className="text-muted">
                <IntlMessages id="home.summary.discount" />
              </span>
              <span className="text-primary">
                {hasDiscount ? (
                  <>-{AddCommaNumber(total_discount_price)}</>
                ) : (
                  <i className="simple-icon-plus" />
                )}
              </span>
            </div>

            {/* Tax */}
            <div
              className="d-flex justify-content-between align-items-center mb-2 cursor-pointer"
              onClick={() => handleToggleModalDiscountTaxFee("tax")}
            >
              <span className="text-muted">
                <IntlMessages id="home.summary.tax" />
              </span>
              <span className="text-primary">
                {hasTax ? (
                  <>+{AddCommaNumber(total_taxfee_price)}</>
                ) : (
                  <i className="simple-icon-plus" />
                )}
              </span>
            </div>

            {/* Fee other */}
            <div
              className="d-flex justify-content-between align-items-center mb-2 cursor-pointer"
              onClick={() => handleToggleModalDiscountTaxFee("fee")}
            >
              <span className="text-muted">
                {hasFee ? (
                  feeOtherLabel
                ) : (
                  <IntlMessages id="home.summary.fee-other" />
                )}
              </span>
              <span className="text-primary">
                {hasFee ? (
                  <>+{AddCommaNumber(total_fee_other_price)}</>
                ) : (
                  <i className="simple-icon-plus" />
                )}
              </span>
            </div>

            {/* Shipping fee */}
            {hasShipping && (
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-primary">
                  <i className="iconsminds-scooter mr-1" />
                  <IntlMessages id="home.summary.shipping" />
                </span>
                <span className="text-primary font-weight-bold">
                  +{AddCommaNumber(shipping_fee)}
                </span>
              </div>
            )}

            {/* Total Payment (if adjustments exist) */}
            {hasAdjustments && (
              <div
                className="d-flex justify-content-between align-items-center pt-2 mb-2"
                style={{ borderTop: "1px solid #e8e8e8" }}
              >
                <strong>
                  <IntlMessages id="home.summary.payment-total" />
                </strong>
                <strong style={{ fontSize: "1.1rem" }}>
                  {AddCommaNumber(totalPayment)}
                </strong>
              </div>
            )}
          </div>
        </PerfectScrollbar>
      </ModalBody>
      <ModalFooter className="d-flex flex-column p-3">
        {/* E-Invoice Checkbox */}
        {eInvoiceEnabled && (
          <div className="d-flex align-items-center justify-content-between mb-2 w-100">
            <CustomInput
              type="checkbox"
              id="issue-einvoice-checkbox-mobile"
              checked={issueEInvoice}
              onChange={(e) =>
                onChangeIssueEInvoice && onChangeIssueEInvoice(e.target.checked)
              }
              label={
                <span className="d-flex align-items-center">
                  <IntlMessages id="einvoice.checkbox.issue" />
                </span>
              }
            />
            {issueEInvoice && (
              <Button
                color="link"
                size="xs"
                className="p-0 text-primary"
                onClick={onOpenEInvoiceInfoModal}
              >
                <i className="simple-icon-note mr-1" />
                Thông tin VAT
              </Button>
            )}
          </div>
        )}
        {Boolean(
          customOrderDate &&
            moment(customOrderDate).format("YYYY-MM-DD") <
              moment().format("YYYY-MM-DD"),
        ) && (
          <div
            className="text-muted font-italic mb-2"
            style={{ fontSize: "11px" }}
          >
            <i className="simple-icon-info mr-1 text-warning" />
            Đơn lùi ngày: HĐĐT sẽ xuất theo ngày hiện tại ({moment().format("DD/MM/YYYY")})
          </div>
        )}

        {/* Row 1: Payment Method & Staff / Shipping selectors */}
        <div className="d-flex w-100" style={{ gap: "6px" }}>
          {/* Payment method */}
          <div style={{ flex: 1 }}>
            <Select
              className="react-select"
              classNamePrefix="react-select"
              name="paymentMethod"
              placeholder={intl.formatMessage({
                id: "home.placeholder.payment",
              })}
              options={PAYMENT_METHOD_OPTIONS}
              value={
                PAYMENT_METHOD_OPTIONS.find(
                  (opt) => opt.value === paymentMethod,
                ) || PAYMENT_METHOD_OPTIONS[0]
              }
              onChange={(selected) =>
                onChangePaymentMethod &&
                onChangePaymentMethod(selected?.value || "cash")
              }
              isSearchable={false}
              formatOptionLabel={formatOptionLabel}
              menuPlacement="top"
              styles={selectStyles}
            />
          </div>
          {/* Staff selector - only for parent accounts */}
          {!isCashierAccount && (
            <div style={{ flex: 1 }}>
              <Select
                className="react-select"
                classNamePrefix="react-select"
                name="selectedStaff"
                placeholder={intl.formatMessage({
                  id: "home.placeholder.staff",
                })}
                options={staffList || []}
                value={
                  (staffList || []).find(
                    (opt) => opt.value === selectedStaff,
                  ) || null
                }
                onChange={(selected) =>
                  onChangeStaff && onChangeStaff(selected?.value || null)
                }
                isSearchable={true}
                isClearable={true}
                formatOptionLabel={({ label }) => label}
                menuPlacement="top"
                styles={selectStyles}
              />
            </div>
          )}
          {/* Shipping selector */}
          <div style={{ flex: 1 }}>
            <Select
              className="react-select"
              classNamePrefix="react-select"
              name="shippingCarrier"
              placeholder={intl.formatMessage({
                id: "home.placeholder.shipping",
              })}
              options={[
                {
                  value: null,
                  label: intl.formatMessage({
                    id: "home.option.shipping.select",
                  }),
                  icon: "",
                },
                ...SHIPPING_CARRIER_OPTIONS.filter((opt) =>
                  connected_providers.some((p) => p.provider === opt.value),
                ),
              ]}
              value={
                SHIPPING_CARRIER_OPTIONS.find(
                  (opt) => opt.value === shippingCarrier,
                ) || {
                  value: null,
                  label: intl.formatMessage({
                    id: "home.option.shipping.select",
                  }),
                  icon: "",
                }
              }
              onChange={(selected) =>
                onChangeShippingCarrier &&
                onChangeShippingCarrier(selected?.value || null)
              }
              isSearchable={false}
              formatOptionLabel={formatOptionLabel}
              menuPlacement="top"
              styles={selectStyles}
            />
          </div>
        </div>

        <hr className="w-100 my-2" style={{ borderTop: "1px solid #e8e8e8" }} />

        {/* Row 2: Customer info */}
        <Row className="mb-3 flex-nowrap w-100">
          <Colxx sm="6" className="pr-1 pl-0">
            <Input
              onChange={onChange}
              value={name}
              placeholder={intl.formatMessage({
                id: "home.label.customer-name",
              })}
              name="customer_name"
              className="border-top-0 border-right-0 border-left-0 rounded-0 p-1"
            />
          </Colxx>
          <Colxx sm="6" className="pl-1 pr-0">
            <Input
              onChange={onChange}
              value={phone}
              placeholder={intl.formatMessage({ id: "home.label.phone" })}
              name="customer_phone"
              className="border-primary border-top-0 border-right-0 border-left-0 rounded-0 p-1"
            />
          </Colxx>
        </Row>

        {/* Row 3: Action buttons */}
        <div className="d-flex w-100" style={{ gap: "8px" }}>
          {/* Save Provisional */}
          <Button
            outline
            color="primary"
            className="flex-shrink-0"
            onClick={handleSaveProvisional}
            style={{ whiteSpace: "nowrap" }}
          >
            {/* <i className="simple-icon-note mr-1" /> */}
            <IntlMessages id="home.btn.save-provisional" />
          </Button>
          {/* Order now */}
          <Button
            className="flex-grow-1"
            color="primary"
            onClick={
              refund_to_customer < 0 && (name === "" || phone === "")
                ? null
                : () => handleSubmit()
            }
          >
            {intl.formatMessage({ id: "home.btn.order-now" })}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default injectIntl(ProductOrderModalMenu);
